import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Building2, Users, Briefcase, Database } from 'lucide-react';
import {
  getSuperAdminOrganizationsApi,
  createSuperAdminOrganizationApi,
  createSuperAdminOrganizationDatabaseApi,
  updateSuperAdminOrganizationApi,
  deleteSuperAdminOrganizationApi,
} from '../../api/api';
import { showMessage } from '../../context/MessageContext.jsx';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { usePagination } from '../../hooks/usePagination.js';
import './OrganizationsList.css';

export default function OrganizationsList() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    tallyId: '',
    ownerName: '',
    ownerEmail: '',
    contactNumber: '',
    aadharNumber: '',
    userAccessCount: 0,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;
  const { totalPages, goToPage, goToNextPage, goToPreviousPage } = usePagination(
    totalCount,
    limit,
    currentPage,
    setCurrentPage
  );

  useEffect(() => {
    fetchOrganizations();
  }, [currentPage, search]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: limit,
        ...(search && { search }),
      };
      const res = await getSuperAdminOrganizationsApi(params);
      setOrganizations(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err) {
      showMessage('Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (org = null) => {
    if (org) {
      setEditingOrg(org);
      setFormData({
        tallyId: org.tallyId || '',
        ownerName: org.ownerName || '',
        ownerEmail: org.ownerEmail || '',
        contactNumber: org.contactNumber || '',
        aadharNumber: org.aadharNumber || '',
        userAccessCount: org.userAccessCount || 0,
      });
    } else {
      setEditingOrg(null);
      setFormData({
        tallyId: '',
        ownerName: '',
        ownerEmail: '',
        contactNumber: '',
        aadharNumber: '',
        userAccessCount: 0,
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setFormData({
      tallyId: '',
      ownerName: '',
      ownerEmail: '',
      contactNumber: '',
      aadharNumber: '',
      userAccessCount: 0,
      billWise: false,
      userWise: false,
      beatWise: false,
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    // Validation
    const errors = {};
    if (!formData.tallyId.trim()) errors.tallyId = 'Tally ID is required';
    if (!formData.ownerName.trim()) errors.ownerName = 'Owner Name is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
      };
      
      if (editingOrg) {
        await updateSuperAdminOrganizationApi(editingOrg.id, submitData);
        showMessage('Organization updated successfully', 'success');
      } else {
        await createSuperAdminOrganizationApi(submitData);
        showMessage('Organization created successfully', 'success');
      }
      handleCloseModal();
      fetchOrganizations();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Operation failed';
      showMessage(errorMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Are you sure you want to delete organization "${org.ownerName}"?`)) {
      return;
    }

    try {
      await deleteSuperAdminOrganizationApi(org.id || org.tallyId);
      showMessage('Organization deleted successfully', 'success');
      fetchOrganizations();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Failed to delete organization';
      showMessage(errorMsg, 'error');
    }
  };

  const handleCreateDatabase = async (org) => {
    if (window.confirm(`Create database for organization "${org.ownerName}" (Tally ID: ${org.tallyId})?`)) {
      try {
        setSubmitting(true);
        const res = await createSuperAdminOrganizationDatabaseApi(org.id);
        showMessage(res.data?.message || 'Database created successfully', 'success');
        fetchOrganizations();
      } catch (err) {
        showMessage(err.response?.data?.message || 'Failed to create database', 'error');
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="organizations-list">
      <div className="organizations-list-header">
        <div>
          <h1 className="organizations-list-title">Organizations</h1>
          <p className="organizations-list-subtitle">Manage all organizations</p>
        </div>
        <div className="organizations-list-header-actions">
          <Link to="/superadmin/users">
            <Button
              variant="secondary"
              className="organizations-list-view-all-button"
            >
              <Users size={18} />
              View All Users
            </Button>
          </Link>
          <Button
            variant="primary"
            onClick={() => handleOpenModal()}
            className="organizations-list-add-button"
          >
            <Plus size={18} />
            Add Organization
          </Button>
        </div>
      </div>

      <Card className="organizations-list-filters">
        <div className="organizations-list-search">
          <Search size={20} className="organizations-list-search-icon" />
          <Input
            type="text"
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="organizations-list-search-input"
          />
        </div>
      </Card>

      {loading ? (
        <div className="organizations-list-loading">
          <div className="spinner"></div>
          <p>Loading organizations...</p>
        </div>
      ) : organizations.length === 0 ? (
        <Card className="organizations-list-empty">
          <Building2 size={48} />
          <p>No organizations found</p>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <Plus size={18} />
            Add First Organization
          </Button>
        </Card>
      ) : (
        <>
          <div className="organizations-list-grid">
            {organizations.map((org) => (
              <Card key={org.id} className="organizations-list-item">
                <div className="organizations-list-item-header">
                  <div className="organizations-list-item-title">
                    <Building2 size={20} />
                    <h3>{org.ownerName}</h3>
                  </div>
                  <div className="organizations-list-item-actions">
                    <button
                      className="organizations-list-item-action"
                      onClick={() => handleOpenModal(org)}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="organizations-list-item-action danger"
                      onClick={() => handleDelete(org)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="organizations-list-item-body">
                  <div className="organizations-list-item-field">
                    <span className="organizations-list-item-label">Tally ID:</span>
                    <span className="organizations-list-item-value">{org.tallyId}</span>
                  </div>
                  <div className="organizations-list-item-field">
                    <span className="organizations-list-item-label">Email:</span>
                    <span className="organizations-list-item-value">{org.ownerEmail || 'N/A'}</span>
                  </div>
                  <div className="organizations-list-item-field">
                    <span className="organizations-list-item-label">Contact:</span>
                    <span className="organizations-list-item-value">{org.contactNumber || 'N/A'}</span>
                  </div>
                  <div className="organizations-list-item-field">
                    <span className="organizations-list-item-label">User Access Count:</span>
                    <span className="organizations-list-item-value">{org.userAccessCount || 0}</span>
                  </div>
                </div>
                <div className="organizations-list-item-footer">
                  <Link
                    to={`/superadmin/organizations/${org.id}/companies`}
                    className="organizations-list-item-link"
                  >
                    <Briefcase size={16} />
                    Companies
                  </Link>
                  <Link
                    to={`/superadmin/organizations/${org.id}/users`}
                    className="organizations-list-item-link"
                  >
                    <Users size={16} />
                    Users
                  </Link>
                  <button
                    className="organizations-list-item-link"
                    onClick={() => handleCreateDatabase(org)}
                    title="Create Database"
                    disabled={submitting}
                    type="button"
                  >
                    <Database size={16} />
                    Create DB
                  </button>
                </div>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              onNextPage={goToNextPage}
              onPreviousPage={goToPreviousPage}
            />
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="organizations-list-modal-overlay" onClick={handleCloseModal}>
          <div className="organizations-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="organizations-list-modal-header">
              <h2>{editingOrg ? 'Edit Organization' : 'Add Organization'}</h2>
              <button className="organizations-list-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="organizations-list-modal-form">
              <FormField
                label="Tally ID *"
                error={formErrors.tallyId}
              >
                <Input
                  type="text"
                  value={formData.tallyId}
                  onChange={(e) => setFormData({ ...formData, tallyId: e.target.value })}
                  placeholder="Enter Tally ID"
                  disabled={!!editingOrg}
                  required
                />
              </FormField>

              <FormField
                label="Owner Name *"
                error={formErrors.ownerName}
              >
                <Input
                  type="text"
                  value={formData.ownerName}
                  onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                  placeholder="Enter owner name"
                  required
                />
              </FormField>

              <FormField
                label="Owner Email"
                error={formErrors.ownerEmail}
              >
                <Input
                  type="email"
                  value={formData.ownerEmail}
                  onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                  placeholder="Enter owner email"
                />
              </FormField>

              <FormField
                label="Contact Number"
                error={formErrors.contactNumber}
              >
                <Input
                  type="text"
                  value={formData.contactNumber}
                  onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                  placeholder="Enter contact number"
                />
              </FormField>

              <FormField
                label="Aadhar Number"
                error={formErrors.aadharNumber}
              >
                <Input
                  type="text"
                  value={formData.aadharNumber}
                  onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
                  placeholder="Enter Aadhar number"
                />
              </FormField>

              <FormField
                label="User Access Count"
                error={formErrors.userAccessCount}
              >
                <Input
                  type="number"
                  value={formData.userAccessCount}
                  onChange={(e) => setFormData({ ...formData, userAccessCount: parseInt(e.target.value) || 0 })}
                  placeholder="Enter user access count"
                  min="0"
                />
              </FormField>

              <div className="organizations-list-modal-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                >
                  {editingOrg ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

